import { useEffect, useRef, useState } from 'react';
import {
  isAttachmentReady,
  projectService,
  toFileIndexState,
  type FileIndexState,
} from '@ideoz/core';

/** How often to re-poll while the scope's files are still indexing. */
const POLL_INTERVAL_MS = 4_000;
/** Give up after this long — files keep processing server-side regardless. */
const MAX_WAIT_MS = 3 * 60 * 1000;

export interface ScopeIndexingRow {
  name: string;
  state: FileIndexState;
}

export interface UseScopeIndexingResult {
  /** One row per file, with its live indexing state. */
  rows: ScopeIndexingRow[];
  /** Files that have reached READY (capped at `total`). */
  indexed: number;
  /** Files being indexed right now. */
  analyzing: number;
  /** Expected file count for this scope. */
  total: number;
}

/**
 * Polls a just-created scope's attachments until every file is indexed, exposing
 * per-file status for the analyzing gate. When all files are ready (or the
 * safety timeout elapses) `onComplete` fires once with the scope id.
 *
 * Pass `scopeId: null` to stay idle (nothing to index yet).
 */
export function useScopeIndexing(params: {
  startupId?: string;
  scopeId: string | null;
  /** Local file names, shown as "pending" until the first poll lands. */
  fileNames: string[];
  onComplete: (scopeId: string) => void;
}): UseScopeIndexingResult {
  const { startupId, scopeId, fileNames, onComplete } = params;
  const [statuses, setStatuses] = useState<ScopeIndexingRow[]>([]);
  // Ref so the poll effect can complete without re-subscribing when the
  // callback identity changes between renders.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const total = fileNames.length;

  useEffect(() => {
    if (!scopeId || !startupId) return;
    setStatuses([]);
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const startedAt = Date.now();

    const finish = () => {
      if (cancelled) return;
      onCompleteRef.current(scopeId);
    };

    const poll = async () => {
      try {
        const items = await projectService.getScopeAttachments(startupId, scopeId);
        if (cancelled) return;
        setStatuses(
          items.map((a) => ({ name: a.name, state: toFileIndexState(a.processingStatus) }))
        );
        const ready = items.filter((a) => isAttachmentReady(a.processingStatus)).length;
        if (items.length >= total && ready >= total) {
          finish();
          return;
        }
      } catch {
        // Transient error — keep polling.
      }
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        finish();
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [scopeId, startupId, total]);

  // Before the first poll lands, show the local names as pending so the list
  // appears instantly.
  const rows: ScopeIndexingRow[] =
    statuses.length > 0 ? statuses : fileNames.map((name) => ({ name, state: 'pending' as const }));
  const indexed = Math.min(rows.filter((r) => r.state === 'indexed').length, total);
  const analyzing = rows.filter((r) => r.state === 'analyzing').length;

  return { rows, indexed, analyzing, total };
}
